/**
 * E-mail address rules of the game's e-mail sign-in (auth.emailSignUp / auth.emailSignIn / auth.emailChange /
 * auth.passwordResetRequest). ONE module, browser-safe (no Node, DOM or protocol imports): the server applies it
 * authoritatively (src/server/core/services/email.ts) and the game UI uses it for instant feedback. The reasons are
 * stable `details.reason` values of the protocol's VALIDATION_FAILED errors (src/shared/protocol/reasons.ts).
 *
 *  - The input is trimmed; 3..254 characters (RFC 5321 path limit), the local part at most 64.
 *  - ASCII addresses only (internationalized addresses are refused, not mangled): a local part of letters, digits and
 *    . ! # $ % & ' * + / = ? ^ _ ` { | } ~ - (no leading, trailing or double dot), one @, and a domain of at least two
 *    labels (letters, digits, inner hyphens; each 1..63 characters) whose last label has two or more letters.
 *  - The address is kept as typed (`email`); uniqueness and the sign-in lookup use the lower-case `key` (mail
 *    providers treat the local part case-insensitively in practice). Plus tags and dots are NOT folded: they are
 *    different addresses as far as the game is concerned.
 */

export const EMAIL_MIN = 3;
export const EMAIL_MAX = 254;
const LOCAL_MAX = 64;
/** Bound of the request payload schema: longer input is a malformed payload (BAD_REQUEST 'invalid_payload'). */
export const EMAIL_INPUT_MAX = 512;

export type EmailProblem = 'email_required' | 'email_too_long' | 'email_invalid';
export const EMAIL_PROBLEMS: readonly EmailProblem[] = ['email_required', 'email_too_long', 'email_invalid'];

export type EmailCheck = { ok: true; email: string; key: string } | { ok: false; field: 'email'; reason: EmailProblem };

const LOCAL = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;
const LABEL = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/;
const TLD = /^[A-Za-z]{2,63}$/;

/** The lookup key of an address (trimmed, lower case). */
export function emailKey(raw: string): string {
  return String(raw).trim().toLowerCase();
}

/** Checks an address as typed (see the module comment). */
export function checkEmail(raw: unknown): EmailCheck {
  const email = typeof raw === 'string' ? raw.trim() : '';
  if (email === '') return { ok: false, field: 'email', reason: 'email_required' };
  if (email.length > EMAIL_MAX) return { ok: false, field: 'email', reason: 'email_too_long' };
  const at = email.lastIndexOf('@');
  if (email.length < EMAIL_MIN || at <= 0 || at !== email.indexOf('@')) return { ok: false, field: 'email', reason: 'email_invalid' };
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const labels = domain.split('.');
  const valid = local.length <= LOCAL_MAX && LOCAL.test(local) && labels.length >= 2 && labels.every((l) => LABEL.test(l)) && TLD.test(labels[labels.length - 1]);
  if (!valid) return { ok: false, field: 'email', reason: 'email_invalid' };
  return { ok: true, email, key: emailKey(email) };
}

/**
 * The address with most of its local part hidden ("a***z@example.org"), for answers that must not hand a whole
 * address to someone who only holds a link, and for logs.
 */
export function maskEmail(address: string): string {
  const at = address.lastIndexOf('@');
  if (at <= 0) return '***';
  const local = address.slice(0, at);
  const shown = local.length <= 2 ? local[0] : `${local[0]}***${local[local.length - 1]}`;
  return `${local.length <= 2 ? `${shown}***` : shown}@${address.slice(at + 1)}`;
}

/** True when a sign-in field holds an address rather than a nickname (nicknames never contain "@"). */
export function looksLikeEmail(text: string): boolean {
  return String(text).includes('@');
}
